import React, { useEffect, useState } from 'react';
import { Route, Switch, Router } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { Analytics } from "@vercel/analytics/react";
import { queryClient } from "./lib/queryClient";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Home from "@/pages/Home";
import UsedMachinery from "@/pages/UsedMachinery";
import NewMachinery from "@/pages/NewMachinery";
import ProductDetail from "@/pages/ProductDetail";
import AdminLogin from "@/pages/AdminLogin";
import AdminPanel from "@/pages/AdminPanel";
import AdminNewProductForm from "@/pages/AdminNewProductForm";
import AdminUsedProductForm from "@/pages/AdminUsedProductForm";
import Catalogs from "@/pages/catalogs";
import NotFound from "@/pages/not-found";
import Nosotros from "@/pages/Nosotros";
import AvisoLegal from "@/pages/AvisoLegal";
import PoliticaPrivacidad from "@/pages/PoliticaPrivacidad";
import PoliticaCookies from "@/pages/PoliticaCookies";
import Garantia from "@/pages/Garantia";
import CondicionesGenerales from "@/pages/CondicionesGenerales";
import ConciliacionPrevia from "@/pages/ConciliacionPrevia";
import TrabajeConNosotros from "@/pages/TrabajeConNosotros";
import Visitanos from "@/pages/Visitanos";
import Contacto from "@/pages/Contacto";
import ExitIntent from "@/components/ExitIntent";
import LoadingSpinner from "@/components/LoadingSpinner";
import Head from "@/components/Head";
import PiensaEnVerde from "@/pages/PiensaEnVerde";
import ServicioB2B from "@/pages/ServicioB2B";
import PlanRenove from "@/pages/PlanRenove";
import UserProfile from "@/pages/UserProfile";
import UserDashboard from "@/pages/UserDashboard";
import DebugPage from './pages/Debug';
import ViewCatalog from '@/pages/ver-catalogo/[slug]';
import TeamPage from './pages/Team';

function GridBackground() {
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const overlay = document.querySelector('.grid-overlay');
      if (overlay) {
        const x = (e.clientX / window.innerWidth) * 100;
        const y = (e.clientY / window.innerHeight) * 100;
        overlay.setAttribute('style', `--mouse-x: ${x}%; --mouse-y: ${y}%`);
        overlay.classList.add('active');
      }
    };

    const handleMouseLeave = () => {
      const overlay = document.querySelector('.grid-overlay');
      if (overlay) {
        overlay.classList.remove('active');
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    document.body.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      document.body.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return (
    <>
      <div className="grid-background grid-animation" />
      <div className="grid-overlay" />
    </>
  );
}

// Función para hacer coincidir rutas con comodines
const useHashLocation = () => {
  const [location, setLocation] = useState(window.location.pathname);

  useEffect(() => {
    // Actualizar la ubicación cuando cambia la URL
    const handleLocationChange = () => {
      setLocation(window.location.pathname);
    };

    window.addEventListener('popstate', handleLocationChange);

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
    };
  }, []);

  // Función para navegar a una nueva ruta
  const navigate = (to: string) => {
    window.history.pushState(null, '', to);
    setLocation(to);
  };

  return [location, navigate] as [string, (to: string) => void];
};

function App() {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Google Analytics Script Injection
    const scriptGtag = document.createElement('script');
    scriptGtag.src = 'https://www.googletagmanager.com/gtag/js?id=G-C67HJKZV20';
    scriptGtag.async = true;
    document.head.appendChild(scriptGtag);

    const scriptInline = document.createElement('script');
    scriptInline.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-C67HJKZV20');
    `;
    document.head.appendChild(scriptInline);

    // Asegurarnos de que el DOM está completamente cargado
    if (document.readyState === 'complete') {
      setIsLoading(false);
    } else {
      window.addEventListener('load', () => setIsLoading(false));
      // Timeout de seguridad por si el evento load no se dispara
      const timer = setTimeout(() => setIsLoading(false), 2000);
      return () => {
        window.removeEventListener('load', () => setIsLoading(false));
        clearTimeout(timer);
      };
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <Head />
      <div className="bg-[#0A0A0B] min-h-screen">
        <Router hook={useHashLocation}>
          <div className="min-h-screen flex flex-col bg-[#0A0A0B]">
            <GridBackground />
            <Switch>
              <Route path="/admin/login" component={AdminLogin} />
              <Route path="/admin" component={AdminPanel} />
              <Route path="/admin/products/new/add" component={AdminNewProductForm} />
              <Route path="/admin/products/used/add" component={(props) => <AdminUsedProductForm {...props} productId={null} onComplete={() => { /* Placeholder */ }} />} />
              <Route path="/mi-dashboard" component={UserDashboard} />
              <Route path="/ver-catalogo/:slug" component={ViewCatalog} />
              <Route>
                <>
                  <Navbar />
                  <main className="flex-1 bg-[#0A0A0B] pt-[120px] transition-[padding] duration-500">
                    <Switch>
                      <Route path="/" component={Home} />
                      <Route path="/used" component={UsedMachinery} />
                      <Route path="/new" component={NewMachinery} />
                      <Route path="/new/category/:categoryId" component={NewMachinery} />
                      <Route path="/new/category/:categoryId/subcategory/:subcategoryId" component={NewMachinery} />
                      <Route path="/new/category/:categoryId/subcategory/:subcategoryId/product/:productId" component={NewMachinery} />
                      <Route path="/catalogos" component={Catalogs} />
                      <Route path="/nosotros" component={Nosotros} />
                      <Route path="/contacto" component={Contacto} />
                      <Route path="/equipo" component={TeamPage} />
                      <Route path="/aviso-legal" component={AvisoLegal} />
                      <Route path="/politica-privacidad" component={PoliticaPrivacidad} />
                      <Route path="/politica-cookies" component={PoliticaCookies} />
                      <Route path="/garantia" component={Garantia} />
                      <Route path="/product/:id" component={ProductDetail} />
                      <Route path="/condiciones-generales" component={CondicionesGenerales} />
                      <Route path="/conciliacion-previa" component={ConciliacionPrevia} />
                      <Route path="/trabaje-con-nosotros" component={TrabajeConNosotros} />
                      <Route path="/visitanos" component={Visitanos} />
                      <Route path="/piensa-en-verde" component={PiensaEnVerde} />
                      <Route path="/servicios/b2b" component={ServicioB2B} />
                      <Route path="/servicios/plan-renove" component={PlanRenove} />
                      <Route path="/mi-perfil" component={UserProfile} />
                      <Route path="/debug-admin" component={DebugPage} />
                      <Route component={NotFound} />
                    </Switch>
                  </main>
                  <Footer />
                </>
              </Route>
            </Switch>
          </div>
          <ExitIntent />
        </Router>
      </div>
      <Toaster />
      <Analytics />
    </QueryClientProvider>
  );
}

export default App;
